import { memo, useState, useEffect } from "react";
import { useI18n } from "@/i18n";
import type { Locale } from "@/i18n";
import { useRouter } from "@/application/stores/routerStore";
import axios from "axios";
import { CONFIG } from "@/config";
import { colors, typography, transitions } from "@/design-system/tokens";

const AUTH_URL = CONFIG.AUTH_URL;

type Step = 1 | 2 | 3 | 4;

export const ForgotPasswordPage = memo(function ForgotPasswordPage() {
  const { t, locale, setLocale } = useI18n();
  const { navigate } = useRouter();
  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Warn on page reload in steps 2-3
  useEffect(() => {
    if (step === 2 || step === 3) {
      const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
      window.addEventListener("beforeunload", handler);
      return () => window.removeEventListener("beforeunload", handler);
    }
  }, [step]);

  // Step 1: Send code
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${AUTH_URL}/v1/auth/forgot-password`, { email, appId: "NEXO_WAREHOUSE" }, { timeout: 10_000 });
    } catch {
      // Always advance — don't reveal if email exists
    }
    setLoading(false);
    setStep(2);
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post<{ resetToken: string }>(
        `${AUTH_URL}/v1/auth/verify-reset-otp`,
        { email, otp },
        { timeout: 10_000 }
      );
      setResetToken(res.data.resetToken);
      setStep(3);
    } catch {
      setError(t.forgotPassword.otpError);
    }
    setLoading(false);
  };

  // Step 3: Reset password
  const checks = {
    min8: password.length >= 8,
    lower: /[a-z]/.test(password),
    upper: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^a-zA-Z0-9]/.test(password),
  };
  const allValid = checks.min8 && checks.lower && checks.upper && checks.number && checks.special;
  const matches = password === confirm && confirm.length > 0;
  const canSubmitPassword = allValid && matches;

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitPassword) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${AUTH_URL}/v1/auth/reset-password`,
        { resetToken, newPassword: password },
        { timeout: 10_000 }
      );
      setStep(4);
    } catch {
      setError(t.forgotPassword.otpError);
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f7f7f7", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px", position: "relative" }}>
      {/* Locale switcher */}
      <div style={{ position: "absolute", top: "20px", right: "24px", display: "flex", gap: "4px" }}>
        {(["es", "en"] as Locale[]).map(l => (
          <button key={l} onClick={() => setLocale(l)} style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.bold, padding: "5px 10px", borderRadius: "6px", border: "none", cursor: "pointer", background: locale === l ? "#111" : "#e8e8e8", color: locale === l ? "#fff" : "#888", transition: `all ${transitions.fast}` }}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>

      <div style={{ background: "#fff", borderRadius: "24px", padding: "48px 40px", width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", alignItems: "center", gap: "24px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
        {/* Icon */}
        <div style={{ width: "52px", height: "52px", background: colors.primary, borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        {/* Step 1: Email */}
        {step === 1 && (
          <form onSubmit={handleSendCode} style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ textAlign: "center" }}>
              <h1 style={{ fontFamily: typography.fontFamily, fontSize: typography.size["2xl"], fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.forgotPassword.title}</h1>
              <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", margin: "8px 0 0" }}>{t.forgotPassword.subtitle}</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>{t.forgotPassword.emailLabel}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.forgotPassword.emailPlaceholder} required style={inputStyle} />
            </div>
            {error && <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error }}>{error}</span>}
            <button type="submit" disabled={loading || !email.includes("@")} style={btnStyle(loading || !email.includes("@"))}>{loading ? "..." : t.forgotPassword.sendCode}</button>
          </form>
        )}

        {/* Step 2: OTP */}
        {step === 2 && (
          <form onSubmit={handleVerifyOtp} style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ textAlign: "center" }}>
              <h1 style={{ fontFamily: typography.fontFamily, fontSize: typography.size["2xl"], fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.forgotPassword.otpTitle}</h1>
              <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", margin: "8px 0 0" }}>{t.forgotPassword.otpSubtitle}</p>
            </div>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder={t.forgotPassword.otpPlaceholder}
              style={{ ...inputStyle, textAlign: "center", letterSpacing: "0.5em", fontSize: "1.5rem", fontWeight: 700 }}
            />
            {error && <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error, textAlign: "center" }}>{error}</span>}
            <button type="submit" disabled={loading || otp.length !== 6} style={btnStyle(loading || otp.length !== 6)}>{loading ? "..." : t.forgotPassword.verify}</button>
            <button type="button" onClick={() => { setStep(1); setOtp(""); setError(""); }} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#999", background: "transparent", border: "none", cursor: "pointer" }}>{t.forgotPassword.resend}</button>
          </form>
        )}

        {/* Step 3: New password */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ textAlign: "center" }}>
              <h1 style={{ fontFamily: typography.fontFamily, fontSize: typography.size["2xl"], fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.forgotPassword.newPasswordTitle}</h1>
              <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", margin: "8px 0 0" }}>{t.forgotPassword.newPasswordSubtitle}</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>{t.changePassword.newPassword} *</label>
              <div style={{ position: "relative" }}>
                <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" style={{ ...inputStyle, paddingRight: "44px" }} />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#aaa", display: "flex" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px" }}>
                <Check ok={checks.min8} label={t.users.pwMin8} />
                <Check ok={checks.lower} label={t.users.pwLower} />
                <Check ok={checks.upper} label={t.users.pwUpper} />
                <Check ok={checks.number} label={t.users.pwNumber} />
                <Check ok={checks.special} label={t.users.pwSpecial} />
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={labelStyle}>{t.changePassword.confirmPassword} *</label>
              <input type={showPassword ? "text" : "password"} value={confirm} onChange={e => setConfirm(e.target.value)} onPaste={e => e.preventDefault()} autoComplete="new-password" style={{ ...inputStyle, borderColor: confirm.length > 0 && !matches ? colors.error : "#e5e5e5" }} />
              {confirm.length > 0 && !matches && <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error }}>{t.changePassword.mismatch}</span>}
            </div>
            {error && <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error }}>{error}</span>}
            <button type="submit" disabled={loading || !canSubmitPassword} style={btnStyle(loading || !canSubmitPassword)}>{loading ? "..." : t.forgotPassword.submit}</button>
          </form>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", textAlign: "center" }}>
            <div style={{ width: "48px", height: "48px", background: "rgba(34,197,94,0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h1 style={{ fontFamily: typography.fontFamily, fontSize: typography.size["2xl"], fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.forgotPassword.successTitle}</h1>
            <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", margin: 0 }}>{t.forgotPassword.successSubtitle}</p>
            <button onClick={() => navigate("login")} style={{ ...btnStyle(false), marginTop: "8px" }}>{t.forgotPassword.backToLogin}</button>
          </div>
        )}

        {/* Back to login (steps 1-3) */}
        {step !== 4 && (
          <button onClick={() => navigate("login")} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#999", background: "transparent", border: "none", cursor: "pointer" }}>
            {t.login.backHome}
          </button>
        )}
      </div>
    </div>
  );
});

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: ok ? "#16a34a" : "#bbb" }}>{ok ? "✓" : "✗"}</span>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: ok ? "#16a34a" : "#bbb" }}>{label}</span>
    </div>
  );
}

function btnStyle(disabled: boolean): React.CSSProperties {
  return { fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: disabled ? "#ccc" : "#111", border: "none", borderRadius: "10px", padding: "14px", cursor: disabled ? "not-allowed" : "pointer", transition: `background ${transitions.fast}`, width: "100%" };
}

const labelStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" };
const inputStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "12px 16px", outline: "none", width: "100%", boxSizing: "border-box" };
